import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { CustomerArtworkOrder } from "@/hooks/useArtworkOrders";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { getStyleById } from "@/constants/artStyles";
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  ShoppingCart,
  Eye,
  Palette,
  Calendar
} from "lucide-react";

interface ArtworkOrderCardProps {
  order: CustomerArtworkOrder;
  onResume?: (order: CustomerArtworkOrder) => void;
  onReorder?: (order: CustomerArtworkOrder) => void;
  onViewDetails?: (order: CustomerArtworkOrder) => void;
}

export const ArtworkOrderCard = ({ 
  order, 
  onResume, 
  onReorder, 
  onViewDetails 
}: ArtworkOrderCardProps) => {
  const getStatusBadge = () => {
    switch (order.status) {
      case 'pending':
        return <Badge variant="secondary" className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          En attente
        </Badge>;
      case 'generation_in_progress':
        return <Badge variant="secondary" className="flex items-center gap-1">
          <RefreshCw className="w-3 h-3 animate-spin" />
          Génération
        </Badge>;
      case 'completed':
        return <Badge variant="default" className="flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Terminé
        </Badge>;
      case 'cancelled':
        return <Badge variant="destructive" className="flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          Annulé
        </Badge>;
      default:
        return <Badge variant="outline">{order.status}</Badge>;
    }
  };

  const getPaymentStatus = () => {
    switch (order.payment_status) {
      case 'paid':
        return <Badge variant="default" className="bg-green-500">Payé</Badge>;
      case 'pending':
        return <Badge variant="secondary">Paiement en attente</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Annulé</Badge>;
      default:
        return null;
    }
  };

  const formatPrice = (priceCents: number) => {
    return (priceCents / 100).toFixed(2) + ' €';
  };

  const canResume = () => {
    return order.status === 'pending' || 
           (order.status === 'generation_in_progress' && !order.generated_image_url);
  };

  const canReorder = () => {
    return order.status === 'completed' || order.generated_image_url;
  };

  const formatDate = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), { 
      addSuffix: true, 
      locale: fr 
    });
  };

  const getStyleDisplayName = (styleId: string) => getStyleById(styleId)?.name || styleId;

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      <CardContent className="p-0">
        <div className="flex">
          {/* Image Section */}
          <div className="w-32 h-32 relative bg-muted flex-shrink-0">
            {order.generated_image_url ? (
              <img
                src={order.generated_image_url}
                alt="Œuvre générée"
                className="w-full h-full object-cover"
              />
            ) : order.original_image_url ? (
              <img
                src={order.original_image_url}
                alt="Image originale"
                className="w-full h-full object-cover opacity-60"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Palette className="w-8 h-8 text-muted-foreground" />
              </div>
            )}
            {!order.generated_image_url && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-white" />
              </div>
            )}
          </div>

          {/* Content Section */}
          <div className="flex-1 p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-semibold text-sm mb-1">
                  Style {getStyleDisplayName(order.selected_style)}
                </h3>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                  <Calendar className="w-3 h-3" />
                  {formatDate(order.created_at)}
                </div>
              </div>
              <div className="flex flex-col gap-1 items-end">
                {getStatusBadge()}
                {getPaymentStatus()}
              </div>
            </div>

            <div className="space-y-1 text-xs text-muted-foreground">
              <p>Format: {order.format_size}</p>
              <p>Prix: {formatPrice(order.price_cents)}</p>
              {order.frame_option && order.frame_option !== 'none' && (
                <p>Encadrement: {order.frame_option}</p>
              )}
            </div>
          </div>
        </div>
      </CardContent>

      <CardFooter className="p-3 pt-0 flex gap-2">
        {canResume() && onResume && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onResume(order)}
            className="flex-1"
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            Reprendre
          </Button>
        )}
        
        {canReorder() && onReorder && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onReorder(order)}
            className="flex-1"
          >
            <ShoppingCart className="w-3 h-3 mr-1" />
            Recommander
          </Button>
        )}

        {onViewDetails && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onViewDetails(order)}
          >
            <Eye className="w-3 h-3" />
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};